import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Container, Grid, Divider, FormHelperText, IconButton, Card, Box, Chip, Stack, FormGroup, FormControlLabel, Checkbox, Typography, TextField, Button, FormControl, InputLabel, Select, MenuItem, InputAdornment } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { useSnackbar } from '../components/snackbar';
import FormProvider from 'hooks/form/form-provider';
import { useForm, Controller } from 'react-hook-form';
import { LoadingButton } from '@mui/lab';
import { saveB2BProfile } from 'api/users';
import { useAuthDispatch } from '../components/AuthStateProvider';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { copyToClipboard, downloadFromURI } from 'utils/Utils';
import { countries } from '../hooks/form/countries';
import { RHFAutocomplete } from '../hooks/form/rhf-autocomplete';
import Iconify from '../components/iconify';

const INTERPRETER_TYPES = [
    'DM Cardiologist',
    'MD/DNB Medicine',
    'CCT Expert',
];

function B2BProfile({ data, isAdmin, onSubmitDone }) {
    const { enqueueSnackbar } = useSnackbar();
    const { updateLocalStorage } = useAuthDispatch();
    const indianStates = [
        'Andhra Pradesh',
        'Arunachal Pradesh',
        'Assam',
        'Bihar',
        'Chhattisgarh',
        'Goa',
        'Gujarat',
        'Haryana',
        'Himachal Pradesh',
        'Jharkhand',
        'Karnataka',
        'Kerala',
        'Madhya Pradesh',
        'Maharashtra',
        'Manipur',
        'Meghalaya',
        'Mizoram',
        'Nagaland',
        'Odisha',
        'Punjab',
        'Rajasthan',
        'Sikkim',
        'Tamil Nadu',
        'Telangana',
        'Tripura',
        'Uttar Pradesh',
        'Uttarakhand',
        'West Bengal',
        'Andaman and Nicobar Islands',
        'Chandigarh',
        'Dadra and Nagar Haveli',
        'Daman and Diu',
        'Delhi',
        'Lakshadweep',
        'Puducherry',
    ];



    const defaultValues = Object.keys(data)?.length ? {
        ...{ ...data, ...data?.company_data },
        portal_type: data?.portal_type || "0",
        organization_code: data?.business_settings?.org_code || "",
        country_code: data?.country_code ? data?.country_code?.replace(`+`, '') : '91',
        phone_number: data?.phone_number?.replace(`${data?.country_code || '+91'}`, ''),
        TWELVE_LEAD: data.tests_enabled?.split(',').includes('TWELVE_LEAD'),
        LEAD_TWO: data.tests_enabled?.split(',').includes('LEAD_TWO'),
        SPANDAN_LEGACY: data?.devices_enabled?.split(',').includes('SPANDAN_LEGACY'),
        SPANDAN_NEO: data?.devices_enabled?.split(',').includes('SPANDAN_NEO'),
        SPANDAN_PRO: data?.devices_enabled?.split(',').includes('SPANDAN_PRO'),
        SPANDAN_ULTRA_12L: data?.devices_enabled?.split(',').includes('SPANDAN_ULTRA_12L'),
        is_active: data?.is_active,
        is_offline_sdk_mode: data?.is_offline_sdk_mode,
        verifier_token: data?.verifier_token,
        sdk_ui: data?.isUiEnable ?? false,
        usage_limit: data?.usage_limit,
        settings: data?.settings || {
            can_view_data: true,
            can_download_report: true,
            can_request_for_review: true,
            auto_request_expert_review: true,
            has_center: false,
            can_manage_patient: false,
            can_manage_associated_user: false,

        },
        otp: data?.otp,
        interpretation_tat: data?.business_settings?.sla_interpretation_minutes || null,
        interpreter_types: data?.business_settings?.allowed_interpreter_types || [],
        pdf_report_settings: {
            grid_enabled: data?.business_settings?.pdf_report_settings?.grid_enabled ?? false,
            grid_color: data?.business_settings?.pdf_report_settings?.grid_color ?? '#FFCBCB',
            grid_opacity: data?.business_settings?.pdf_report_settings?.grid_opacity ?? 0.4,
            wave_thickness: data?.business_settings?.pdf_report_settings?.wave_thickness ?? 0.1,

        },
    } : {
        portalType: "0",
        country_code: '91',
        phone_number: '',
        name: '',
        email: '',
        business_name: '',
        gst_number: '',
        billing_address: '',
        state: '',
        pin: '',
        code: '',
        organization_code: "",
        sdk_ui: false,
        settings: {
            can_view_data: true,
            can_download_report: true,
            can_request_for_review: true,
            auto_request_expert_review: true,
            has_center: false,
            can_manage_patient: false,
            can_manage_associated_user: data?.settings?.can_manage_associated_user ?? false,

        },
        otp: '',
        interpretation_tat: '',
        interpreter_types: [],
        pdf_report_settings: {
            grid_enabled: false,
            grid_color: '#FFCBCB',
            grid_opacity: 0.4,
            wave_thickness: 0.1,

        },

    };

    const onCopyClick = (text) => (e) => {
        copyToClipboard(text);
        enqueueSnackbar("Copied to clipboard.", { variant: 'success' });
    };




    const methods = useForm({
        defaultValues,
        mode: "onChange",
    });
    useEffect(() => {
        if (data && Object.keys(data).length) {
            methods.reset({
                ...defaultValues,
                sdk_ui: data?.isUiEnable ?? false,
            });
        }
    }, [data]);

    const hasCenter = methods.watch('settings.has_center');
    const canManageAssociatedUser = methods.watch('settings.can_manage_associated_user');

    const gridEnabled = methods.watch('pdf_report_settings.grid_enabled');
    const gridColor = methods.watch('pdf_report_settings.grid_color');
    const gridOpacity = methods.watch('pdf_report_settings.grid_opacity');
    const waveThickness = methods.watch('pdf_report_settings.wave_thickness');



    const portalType = methods.watch(
        'portal_type',
        defaultValues.portal_type || 0
    );

    const {
        control,
        register,
        handleSubmit,
        setValue,
        formState: { errors, isSubmitting },
    } = methods;

    const [showAdditionalFields, setShowAdditionalFields] = useState(false);


    const additionalFields = () => {
        setShowAdditionalFields(!showAdditionalFields);
    };
    const handleTestLimitChange = (event) => {
        methods.setValue('test_charges', event.target.value);
    };

    const onSubmit = handleSubmit(async (formData) => {
        try {
            if (!isAdmin) delete formData?.interpretation_charges;
            formData.country_code = `+${formData?.country_code}`;
            formData.phone_number = `${formData?.country_code}${formData?.phone_number}`;
            formData.organization_code = formData.organization_code?.toUpperCase?.() || "";
            let enabledDevices = Object.keys(formData)
                .filter(key => formData[key] && ['SPANDAN_ULTRA_12L', 'SPANDAN_PRO', 'SPANDAN_NEO', 'SPANDAN_LEGACY'].includes(key))
                .join(',');
            let testsEnabled = Object.keys(formData)
                .filter(key => formData[key] && ['TWELVE_LEAD', 'LEAD_TWO'].includes(key))
                .join(',');

            formData.devices_enabled = enabledDevices;
            formData.tests_enabled = testsEnabled;
            formData.sla_interpretation_minutes = formData.interpretation_tat ?? null;

            formData.allowed_interpreter_types = Array.isArray(formData.interpreter_types)
                ? formData.interpreter_types
                : [];
            delete formData.interpretation_tat;
            delete formData.interpreter_types;

            formData.isUiEnable = formData.sdk_ui;
            delete formData.sdk_ui;

            const response = await saveB2BProfile(formData)
            if (response.success === true && response.status == 200) {
                if (!isAdmin) {
                    // const updatedValues={...data, name: formData?.name, displayName: formData?.name, email: formData?.email, company_data: response?.data?.company_data }
                    // localStorage.setItem('TokenDetails',JSON.stringify(updatedValues))
                    const updatedValues = {
                        updated_devices_enabled: formData?.enableddevices, updated_tests_enabled: formData?.testsEnabled
                        , name: formData?.name, displayName: formData?.name, email: formData?.email, company_data: response?.data?.company_data
                    };
                    updateLocalStorage('TokenDetails', updatedValues);
                }
                enqueueSnackbar(response.message, { variant: 'success' })
                if (onSubmitDone) onSubmitDone(true)
            } if (response.success === false && response.status == 200 && response.data.orgCodeExists) {
                enqueueSnackbar(response.message, { variant: 'error' })
                if (onSubmitDone) onSubmitDone(null, true)
            }
            else if (response.success === false) {
                enqueueSnackbar("Unable to save the data.", { variant: 'error' })
                if (onSubmitDone) onSubmitDone(null, true)
            }
        } catch (e) {
            console.log(e)
            enqueueSnackbar("Unable to save the data.", { variant: 'error' })
            if (onSubmitDone) onSubmitDone(null, true)
        }
    })

    const handleInterpreterMultiSelect = (_, value) => {
        methods.setValue('interpreter_types', value, {
            shouldDirty: true,
            shouldValidate: true,
        });
    };

    return (
        <Box px={3} pb={3}>
            <FormProvider methods={methods} onSubmit={onSubmit}>
                <Grid container spacing={2} justifyContent='center'>
                    <Grid item md={4} sm={6} xs={12}>
                        <Controller
                            name="portal_type"
                            control={control}
                            rules={{ required: 'Please select a portal type.' }}
                            defaultValue="0"
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    select
                                    label="Portal Type"
                                    fullWidth
                                    error={!!errors.portal_type}
                                    helperText={errors.portal_type?.message}
                                    disabled={Object.keys(data).length > 0}
                                >
                                    <MenuItem value="1">Spandan One</MenuItem>
                                    <MenuItem value="0">Expert Portal</MenuItem>
                                </TextField>
                            )}
                        />

                    </Grid>
                    <Grid item md={4} sm={6} xs={12}>
                        <Controller
                            name="country_code"
                            control={control}
                            render={({ field }) => (
                                <RHFAutocomplete
                                    {...field}
                                    label="Country"
                                    value={field?.value || (defaultValues?.country_code || '91')}
                                    options={countries.map((country) => country.label + "/" + country.phone)}
                                    getOptionLabel={(option) => option}
                                    disabled={!isAdmin}
                                    isOptionEqualToValue={(option, value) =>
                                        option?.split('/')[0] === value || option?.split('/')[1] === value
                                    }
                                    renderOption={(props, option) => {
                                        const { code, label, phone } = countries.find(
                                            (country) => country.label === option?.split('/')[0]
                                        ) || {};

                                        if (!label) return null;

                                        return (
                                            <li {...props} key={label}>
                                                <Iconify
                                                    icon={`circle-flags:${code?.toLowerCase()}`}
                                                    width={28}
                                                    sx={{ mr: 1 }}
                                                />
                                                {label} ({code}) +{phone}
                                            </li>
                                        );
                                    }}

                                />
                            )}
                        />
                    </Grid>
                    <Grid item md={4} sm={6} xs={12}>
                        <Controller
                            name="phone_number"
                            control={control}
                            rules={{
                                required: "Please enter the phone number.",
                                pattern: {
                                    value: /^[0-9]{10}$/i,
                                    message: "Invalid phone number",
                                },
                            }}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    label="Phone Number"
                                    variant="outlined"
                                    fullWidth
                                    sx={{ mt: '0px !important' }}
                                    placeholder="9999999999"
                                    margin="normal"
                                    error={!!errors.phone_number}
                                    helperText={errors.phone_number?.message}
                                    onInput={(e) => e.target.value = e.target.value.replace(/[^0-9]/g, '')}
                                    disabled={!isAdmin}
                                />
                            )}
                        />
                    </Grid>
                    <Grid item md={4} sm={6} xs={12}>
                        <Controller
                            name="name"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    sx={{ minWidth: 70 }}
                                    label="User Name"
                                    fullWidth
                                    {...register('name', {
                                        required: "Please enter business owner's name."
                                    })}
                                    error={!!errors.name}
                                    helperText={errors.name?.message}
                                />
                            )} />
                    </Grid>
                    <Grid item md={4} sm={6} xs={8}>
                        <Controller
                            name="email"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    sx={{ minWidth: 70 }}
                                    label="Email"
                                    fullWidth
                                    {...register('email', {
                                        required: 'Enter the email id',
                                        pattern: {
                                            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                            message: 'Invalid email address'
                                        }
                                    })}
                                    error={!!errors.email}
                                    helperText={errors.email?.message}
                                />
                            )} />
                    </Grid>
                    <Grid item md={4} sm={6} xs={12}>
                        <Controller
                            name="business_name"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    sx={{ minWidth: 70 }}
                                    label="Business/Company Name"
                                    fullWidth
                                    {...register('business_name', {
                                        required: 'Please enter the business name.'
                                    })}
                                    error={!!errors.business_name}
                                    helperText={errors.business_name?.message}
                                />
                            )} />
                    </Grid>

                    <Grid item md={4} sm={6} xs={12}>
                        <Controller
                            name="billing_address"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    sx={{ minWidth: 70 }}
                                    label="Billing Address"
                                    fullWidth
                                    {...register('billing_address', {
                                        required: 'Please enter the billing address.'
                                    })}
                                    error={!!errors.billing_address}
                                    helperText={errors.billing_address?.message}
                                />
                            )} />
                    </Grid>

                    <Grid item md={4} sm={6} xs={12}>
                        <Controller
                            name="state"
                            control={control}
                            defaultValue={defaultValues?.state || ""}
                            rules={{
                                required: 'Please select or enter a state.'
                            }}
                            render={({ field }) => (
                                <Autocomplete
                                    {...field}
                                    freeSolo
                                    id="free-solo-2-demo"
                                    disableClearable
                                    options={indianStates}
                                    onChange={(event, value) => {
                                        // Update field with selected option or typed text
                                        field.onChange(value || event.target.value);
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Select State"
                                            error={!!errors.state}
                                            helperText={errors.state?.message}
                                            inputProps={{
                                                ...params.inputProps,
                                                type: 'text',
                                            }}
                                            onChange={(event) => {
                                                // Handle typing in free text
                                                field.onChange(event.target.value);
                                            }}
                                        />
                                    )}
                                />
                            )}
                        />
                    </Grid>

                    {portalType == 1 && (
                        <Grid item md={4} sm={6} xs={12}>
                            <Controller
                                name="organization_code"
                                control={control}
                                rules={{
                                    required: "Enter the organization code.",
                                    minLength: { value: 2, message: "Code must be exactly 2 characters." },
                                    maxLength: { value: 2, message: "Code must be exactly 2 characters." },
                                    pattern: { value: /^[A-Za-z0-9]{2}$/, message: "Only letters or numbers allowed." }
                                }}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        label="Organization Code"
                                        fullWidth
                                        value={(field.value || "").toUpperCase()}
                                        inputProps={{
                                            maxLength: 2,
                                            style: { textTransform: "uppercase" }
                                        }}
                                        error={!!errors.organization_code}
                                        helperText={errors.organization_code?.message}
                                    />
                                )}
                            />
                        </Grid>
                    )}

                    <Grid item md={4} sm={6} xs={12}>
                        <Controller
                            name="gst_number"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    value={(field?.value || '')?.toUpperCase()}
                                    sx={{ minWidth: 100 }}
                                    label="GST Number"
                                    fullWidth
                                />
                            )} />
                    </Grid>

                    <Grid item md={4} sm={3} xs={6}>
                        <Controller
                            name="pin"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    sx={{ minWidth: 100 }}
                                    label="Pin code"
                                    fullWidth
                                    {...register('pin', {
                                        required: 'Please enter the pin code.',
                                        pattern: {
                                            value: /^[0-9]{6,6}$/i,
                                            message: 'Invalid pin code'
                                        }
                                    })}
                                    error={!!errors.pin}
                                    helperText={errors.pin?.message}
                                />
                            )} />
                    </Grid>
                    <Grid item md={4} sm={3} xs={6}>
                        <Controller
                            name="code"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    sx={{ minWidth: 100 }}
                                    label="GST State code"
                                    fullWidth
                                    {...register('code', {
                                        required: 'Please enter the state code.',
                                        pattern: {
                                            value: /^[0-9]{1,2}$/i,
                                            message: 'Invalid state code'
                                        }
                                    })}
                                    error={!!errors.code}
                                    helperText={errors.code?.message}
                                />
                            )} />
                    </Grid>



                </Grid>
                {isAdmin && (
                    <>
                        <Box py={2}>
                            <Button onClick={additionalFields}>
                                {showAdditionalFields ? (<RemoveIcon sx={{ mr: 1 }} />) : (<AddIcon sx={{ mr: 1 }} />)}Additional details
                            </Button>
                        </Box>
                        {showAdditionalFields && (
                            <Grid container spacing={1} justifyContent='center'>
                                <Grid item xs={12} md={12} sx={{ display: 'flex', flexDirection: 'row', gap: 2, alignItems: 'center' }}>
                                    <FormControl>
                                        <TextField
                                            defaultValue={data?.test_charges}
                                            sx={{ minWidth: 70 }}
                                            onChange={handleTestLimitChange}
                                            label="Test Charges (per test)"
                                            size='small'
                                            error={!!errors.test_charges}
                                            helperText={errors.api_key?.test_charges}
                                        />
                                    </FormControl>
                                    <FormControl>
                                        <TextField
                                            sx={{ minWidth: 70 }}
                                            label="Interpretation charges (per request)"
                                            size='small'
                                            {...register('interpretation_charges', {
                                                pattern: {
                                                    value: /^[0-9]+$/i,
                                                    message: 'Invalid interpretation charge'
                                                }
                                            })}
                                            error={!!errors.interpretation_charges}
                                            helperText={errors.interpretation_charges?.message}
                                        />
                                    </FormControl>
                                    <FormGroup item md={12} sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 2 }}>
                                        <FormControl item md={12} sx={{ display: 'flex', flexDirection: 'row', gap: 2 }}>
                                            <TextField
                                                defaultValue={data?.api_key}
                                                sx={{ minWidth: 70 }}
                                                label="business id/api key"
                                                size='small'
                                                disabled
                                                InputProps={{
                                                    endAdornment: (data?.api_key ?
                                                        <InputAdornment position="start" sx={{ cursor: "pointer" }}>
                                                            <ContentCopyIcon fontSize='small' sx={{ cursor: "pointer" }} onClick={onCopyClick(data?.api_key)} />
                                                        </InputAdornment> : null
                                                    ),
                                                }}
                                            />
                                        </FormControl>
                                    </FormGroup>
                                    <FormControl item md={12} sx={{ display: 'flex', flexDirection: 'row', gap: 2 }}>
                                        <TextField
                                            defaultValue={data?.verifier_token}
                                            sx={{ minWidth: 70 }}
                                            label="Verify Token"
                                            size='small'
                                            disabled
                                            InputProps={{
                                                endAdornment: (data?.verifier_token ?
                                                    <InputAdornment position="start" sx={{ cursor: "pointer" }}>
                                                        <ContentCopyIcon fontSize='small' sx={{ cursor: "pointer" }} onClick={onCopyClick(data?.verifier_token)} />
                                                    </InputAdornment> : null
                                                ),
                                            }}
                                        />
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12}>
                                    <Typography variant="subtitle1" sx={{ fontSize: { xs: '0.8rem', md: '0.9rem' } }}>Settings: </Typography>
                                    <FormGroup item md={12} sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, pb: 1 }}>
                                        <FormControlLabel
                                            control={<Checkbox defaultChecked={defaultValues?.settings?.can_view_data} {...methods.register('settings.can_view_data')} />}
                                            label="Can view tests"
                                        />
                                        <FormControlLabel
                                            control={<Checkbox defaultChecked={defaultValues?.settings?.can_request_for_review} {...methods.register('settings.can_request_for_review')} />}
                                            label="Can request for review"
                                        />
                                        <FormControlLabel
                                            control={<Checkbox defaultChecked={defaultValues?.settings?.can_download_report} {...methods.register('settings.can_download_report')} />}
                                            label="Can download report"
                                        />
                                        <FormControlLabel
                                            control={<Checkbox defaultChecked={defaultValues?.settings?.auto_request_expert_review} {...methods.register('settings.auto_request_expert_review')} />}
                                            label="Auto Requested for Review"
                                        />
                                        <FormControlLabel
                                            control={<Checkbox defaultChecked={defaultValues?.settings?.can_manage_patient} {...methods.register('settings.can_manage_patient')} />}
                                            label="Can Manage Patient"
                                        />
                                        <Box
                                            sx={{
                                                border: '1px dashed',
                                                borderColor: 'grey.400',
                                                borderRadius: 1.5,
                                                p: 2,
                                            }}
                                        >
                                            <FormGroup
                                                sx={{
                                                    display: 'flex',
                                                    flexDirection: { xs: 'column', md: 'row' },
                                                    gap: 3,
                                                }}
                                            >
                                                {/* Has Center(s) */}
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            checked={!!hasCenter}
                                                            onChange={(e) => {
                                                                const checked = e.target.checked;

                                                                methods.setValue('settings.has_center', checked, {
                                                                    shouldDirty: true,
                                                                    shouldTouch: true,
                                                                    shouldValidate: true,
                                                                });

                                                                if (checked) {
                                                                    methods.setValue('settings.can_manage_associated_user', false, {
                                                                        shouldDirty: true,
                                                                        shouldTouch: true,
                                                                        shouldValidate: true,
                                                                    });
                                                                }
                                                            }}
                                                        />
                                                    }
                                                    label="Has Center(s)"
                                                />


                                                {/* Can Manage Associate User */}
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            checked={!!canManageAssociatedUser}
                                                            onChange={(e) => {
                                                                const checked = e.target.checked;

                                                                methods.setValue('settings.can_manage_associated_user', checked, {
                                                                    shouldDirty: true,
                                                                    shouldTouch: true,
                                                                    shouldValidate: true,
                                                                });

                                                                if (checked) {
                                                                    methods.setValue('settings.has_center', false, {
                                                                        shouldDirty: true,
                                                                        shouldTouch: true,
                                                                        shouldValidate: true,
                                                                    });
                                                                }
                                                            }}
                                                        />
                                                    }
                                                    label="Can Manage Associate User"
                                                />

                                            </FormGroup>
                                        </Box>

                                    </FormGroup>
                                </Grid>
                                <Grid item xs={12}>
                                    <Typography variant="subtitle1" sx={{ fontSize: { xs: '0.8rem', md: '0.9rem' } }}>Enabled Devices</Typography>
                                    <FormGroup item md={12} sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' } }}>
                                        <FormControlLabel
                                            control={<Checkbox defaultChecked={data?.devices_enabled?.split(',').includes('SPANDAN_LEGACY')} {...methods.register('SPANDAN_LEGACY')} />}
                                            label="Spandan Lagecy"
                                        />
                                        <FormControlLabel
                                            control={<Checkbox defaultChecked={data?.devices_enabled?.split(',').includes('SPANDAN_NEO')} {...methods.register('SPANDAN_NEO')} />}
                                            label="Spandan Neo"
                                        />
                                        <FormControlLabel
                                            control={<Checkbox defaultChecked={data?.devices_enabled?.split(',').includes('SPANDAN_PRO')} {...methods.register('SPANDAN_PRO')} />}
                                            label="Spandan Pro"
                                        />
                                        <FormControlLabel
                                            control={<Checkbox defaultChecked={data?.devices_enabled?.split(',').includes('SPANDAN_ULTRA_12L')} {...methods.register('SPANDAN_ULTRA_12L')} />}
                                            label="Spandan Ultra12L"
                                        />
                                    </FormGroup>
                                </Grid>

                                <Grid item xs={12} sx={{ display: 'flex', flexDirection: 'row', gap: 2 }}>
                                    <FormGroup item md={12} sx={{ display: 'flex', flexDirection: 'row', gap: 2 }}>
                                        <Grid >
                                            <Typography variant="subtitle1" sx={{ fontSize: { xs: '0.8rem', md: '0.9rem' } }}>Enabled Tests</Typography>
                                            <FormGroup item md={12} sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' } }}>
                                                <FormControlLabel
                                                    control={<Checkbox
                                                        defaultChecked={data?.tests_enabled?.split(',').includes('TWELVE_LEAD')}
                                                        onChange={(e) => setValue('TWELVE_LEAD', e.target.checked)}
                                                    />}
                                                    label="Twelve Lead"
                                                />
                                                <FormControlLabel
                                                    control={<Checkbox
                                                        defaultChecked={data?.tests_enabled?.split(',').includes('LEAD_TWO')}
                                                        onChange={(e) => setValue('LEAD_TWO', e.target.checked)}
                                                    />}
                                                    label="Two Lead"
                                                />
                                            </FormGroup>
                                        </Grid>
                                        <Grid item>
                                            <Typography variant="subtitle1" sx={{ fontSize: { xs: '0.8rem', md: '0.9rem' } }}>Status</Typography>
                                            <FormControlLabel
                                                control={<Checkbox defaultChecked={data?.is_active} onChange={(e) => methods.setValue('is_active', e.target.checked)} />}
                                                label={'Active'}
                                            />
                                        </Grid>
                                        <Grid item>
                                            <Typography variant="subtitle1" sx={{ fontSize: { xs: '0.8rem', md: '0.9rem' } }}>Offline SDK mode</Typography>
                                            <FormControlLabel
                                                control={<Checkbox defaultChecked={data?.is_offline_sdk_mode} onChange={(e) => methods.setValue('is_offline_sdk_mode', e.target.checked)} />}
                                                label={'Enabled'}
                                            />
                                        </Grid>
                                        <Grid item>
                                            <Typography
                                                variant="subtitle1"
                                                sx={{ fontSize: { xs: '0.8rem', md: '0.9rem' } }}
                                            >
                                                SDK UI
                                            </Typography>
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={!!methods.watch('sdk_ui')}
                                                        onChange={(e) =>
                                                            methods.setValue('sdk_ui', e.target.checked, {
                                                                shouldDirty: true,
                                                                shouldTouch: true,
                                                                shouldValidate: true,
                                                            })
                                                        }
                                                    />
                                                }
                                                label="Enabled"
                                            />
                                        </Grid>
                                        <Grid item>
                                            <Typography variant="subtitle1" sx={{ fontSize: { xs: '0.8rem', md: '0.9rem' } }}>Uses Limit</Typography>
                                            <TextField
                                                defaultValue={data?.usage_limit}
                                                size='small'
                                                placeholder='Usage limit'
                                                sx={{ height: 36, width: '40' }}
                                                {...register('usage_limit')}
                                            />
                                        </Grid>
                                        <Grid item>
                                            <Typography
                                                variant="subtitle1"
                                                sx={{ fontSize: { xs: '0.8rem', md: '0.9rem' } }}
                                            >
                                                Fixed OTP
                                            </Typography>

                                            <TextField
                                                size="small"
                                                placeholder="OTP"
                                                {...register('otp', {
                                                    pattern: {
                                                        value: /^[0-9]{6}$/,
                                                        message: 'OTP must be exactly 6 digits',
                                                    },
                                                })}
                                                error={!!errors?.otp}
                                                helperText={errors?.otp?.message}
                                                inputProps={{
                                                    maxLength: 6,
                                                    inputMode: 'numeric',
                                                }}
                                                onInput={(e) => {
                                                    e.target.value = e.target.value.replace(/[^0-9]/g, '');
                                                }}
                                                sx={{ width: 140 }}
                                            />
                                        </Grid>
                                        <Grid item>
                                            <FormControl sx={{ mt: '22px' }}>
                                                <TextField
                                                    size="small"
                                                    type="number"
                                                    sx={{ minWidth: 160 }}
                                                    {...register('interpretation_tat', {
                                                        valueAsNumber: true,
                                                        min: {
                                                            value: 1,
                                                            message: 'Must be at least 1 minute',
                                                        },
                                                    })}
                                                    error={!!errors.interpretation_tat}
                                                    helperText={errors.interpretation_tat?.message}
                                                    label="Interpretation TAT (minutes)"
                                                />
                                            </FormControl>

                                        </Grid>
                                        <Grid item>
                                            <Autocomplete
                                                multiple
                                                disableCloseOnSelect
                                                options={INTERPRETER_TYPES}
                                                value={methods.watch('interpreter_types') || []}
                                                onChange={handleInterpreterMultiSelect}
                                                getOptionLabel={(option) => option}
                                                limitTags={100} // allow wrapping instead of collapsing
                                                sx={{
                                                    width: 360,
                                                    mt: '22px',
                                                    '& .MuiAutocomplete-tag': {
                                                        maxWidth: '48%',
                                                    },
                                                }}
                                                renderOption={(props, option, { selected }) => (
                                                    <li {...props}>
                                                        <Checkbox checked={selected} style={{ marginRight: 8 }} />
                                                        {option}
                                                    </li>
                                                )}
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        label="Allowed Interpreter"
                                                        placeholder="Choose Multiple"
                                                        size="small"
                                                    />
                                                )}
                                            />
                                        </Grid>

                                        {/* <FormControl sx={{ minWidth: 220, mt: '22px' }} size="small">
                                            <Controller
                                                name="interpreter_types"
                                                control={control}
                                                render={({ field }) => (
                                                    <Select
                                                        {...field}
                                                        multiple
                                                        displayEmpty
                                                        renderValue={(selected) => {
                                                            if (!selected || selected.length === 0) {
                                                                return 'Interpreter Type (SLA)';
                                                            }
                                                            return `Selected ${selected.length}`;
                                                        }}
                                                    >
                                                        {['DM Cardiologist', 'MD/DNB Medicine', 'CCT Expert'].map((type) => (
                                                            <MenuItem key={type} value={type}>
                                                                <Checkbox checked={field.value?.includes(type)} />
                                                                <Typography variant="body2">{type}</Typography>
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                )}
                                            />
                                        </FormControl> */}

                                    </FormGroup>
                                </Grid>

                                <Grid item xs={12} textAlign='center'>
                                    <Divider sx={{ my: 2 }} />
                                    <Grid item xs={12}>
                                        <Typography
                                            variant="subtitle1"
                                            sx={{ fontSize: { xs: '0.8rem', md: '0.9rem', display: 'flex' } }}
                                        >
                                            ECG Report Settings:
                                        </Typography>

                                        <Box
                                            sx={{
                                                border: '1px dashed',
                                                borderColor: 'grey.400',
                                                borderRadius: 1.5,
                                                p: 2,
                                                mt: 1,
                                                width: '100%',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 2,
                                                flexWrap: 'wrap',
                                            }}
                                        >
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={!!gridEnabled}
                                                        onChange={(e) => {
                                                            methods.setValue(
                                                                'pdf_report_settings.grid_enabled',
                                                                e.target.checked,
                                                                { shouldDirty: true }
                                                            );
                                                        }}
                                                    />
                                                }
                                                label="Grid Enabled"
                                            />

                                            <FormControl
                                                size="small"
                                                sx={{ minWidth: 220, flex: 1 }}
                                                disabled={!gridEnabled}
                                            >
                                                <InputLabel>Grid Color</InputLabel>

                                                <Select
                                                    label="Grid Color"
                                                    value={gridColor || ''}
                                                    onChange={(e) =>
                                                        methods.setValue(
                                                            'pdf_report_settings.grid_color',
                                                            e.target.value,
                                                            {
                                                                shouldDirty: true,
                                                                shouldTouch: true,
                                                                shouldValidate: true,
                                                            }
                                                        )
                                                    }
                                                >
                                                    {/* <MenuItem value="">
                                                        Select Grid Color
                                                    </MenuItem> */}

                                                    <MenuItem value="#FFCBCB">Light Red</MenuItem>
                                                    <MenuItem value="#121212">Dark</MenuItem>
                                                    <MenuItem value="#FFFFFF">Transparent</MenuItem>
                                                </Select>
                                            </FormControl>
                                            <TextField
                                                size="small"
                                                type="number"
                                                label="Grid Opacity"
                                                sx={{ minWidth: 160, flex: 1 }}
                                                disabled={!gridEnabled}
                                                value={gridOpacity}
                                                inputProps={{
                                                    min: 0,

                                                    step: 0.1,
                                                }}
                                                onChange={(e) =>
                                                    methods.setValue(
                                                        'pdf_report_settings.grid_opacity',
                                                        Number(e.target.value),
                                                        {
                                                            shouldDirty: true,
                                                            shouldTouch: true,
                                                            shouldValidate: true,
                                                        }
                                                    )
                                                }
                                            />

                                            <TextField
                                                size="small"
                                                type="number"
                                                label="Wave Thickness"
                                                sx={{ minWidth: 160, flex: 1 }}
                                                disabled={!gridEnabled}
                                                value={waveThickness}
                                                InputLabelProps={{ shrink: true }}
                                                inputProps={{
                                                    min: 0.1,
                                                    max: 1,
                                                    step: 0.1,
                                                }}
                                                onChange={(e) =>
                                                    methods.setValue(
                                                        'pdf_report_settings.wave_thickness',
                                                        Number(e.target.value),
                                                        {
                                                            shouldDirty: true,
                                                            shouldTouch: true,
                                                            shouldValidate: true,
                                                        }
                                                    )
                                                }
                                            />


                                        </Box>
                                    </Grid>

                                    <Divider sx={{ my: 2 }} />

                                    <LoadingButton
                                        sx={{ minWidth: 150 }}
                                        type="submit"
                                        variant="contained"
                                        loading={isSubmitting}
                                    >
                                        Save
                                    </LoadingButton>
                                </Grid>
                            </Grid>
                        )}
                    </>)}
            </FormProvider>
        </Box>
    )
}

export default B2BProfile;